<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="再読込">
        <v-btn icon @click="$emit('reload')">
          <v-icon>mdi-reload</v-icon>
        </v-btn>
      </el-tooltip>
      <v-spacer/>
      <toolbar-inline>
        <trade-contract-filter
            @apply-filter="tradeContractFilter = $event"
            :trade-contracts="publicTradeContractsView"
            hide-status-filters
            hide-type-filters>
        </trade-contract-filter>
      </toolbar-inline>
      <toolbar-inline>
        <v-text-field
            v-model="filterText"
            clearable
            placeholder="検索">
        </v-text-field>
      </toolbar-inline>
    </template>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        募集中取引契約
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <el-table
        class="public-trade-contract-table"
        :data="publicTradeContractsViewFiltered"
        :default-sort="{prop: 'createdAt', order: 'descending'}"
        :row-class-name="({row}) => row.expireDateIsValid ? '' : 'expired-row'"
        row-key="id"
        @current-change="handleTradeContractSelection"
        @expand-change="handleTradeContractExpanded"
        ref="publicTradeContractsTable"
        highlight-current-row
        :height="tabItemBounds.height"
        border>
      <el-table-column type="expand">
        <template #default="{row}">
          <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
              申込中取引契約
           - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
          <el-table
              class="applying-trade-contract-table"
              :data="applyingTradeContractsView(row)"
              :default-sort="{prop: 'createdAt', order: 'descending'}"
              border>
            <el-table-column
                width="120"
                prop="id"
                class-name="id-column"
                :label="`申込されている\n取引契約のID`">
              <template #header="{column}">
                <div class="id-column-header">{{ column.label }}</div>
              </template>
            </el-table-column>

            <el-table-column
                label="状態"
                align="center"
                prop="statusText"
                width="100"
                sortable>
            </el-table-column>

            <el-table-column
                min-width="150"
                prop="companionOwnerName"
                label="取引先事業者"
                :sort-method="buildSort(row => row.companionOwnerName)"
                sortable>
              <template #default="{row}">
                <table-inline v-if="row.companionTerminalName">
                  <text-field-like
                      style="font-size: 85%;"
                      :value="row.companionTerminalName">
                  </text-field-like>
                </table-inline>
                <table-inline>
                  <text-field-like
                      :value="row.companionOwnerName"
                      placeholder="相手側で未承認"
                      hide-details
                      dense>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>

            <el-table-column
                width="100"
                prop="createdAt"
                label="作成日"
                sortable>
              <template #default="{row}">{{ formatDate(row.createdAt) }}</template>
            </el-table-column>

            <el-table-column
                width="70"
                align="center"
                fixed="right"
                column-key="editing"
                v-if="isEditable">
              <template #default="{row}">
                <icon-btns>
                  <icon-btn
                      v-if="row.status === 'applying'"
                      tooltip="申込承認フロー"
                      icon="mdi-rss"
                      :to="{name: '/applyContract', params: {tradeContractId: row.id}, query: {from: 'public'}}">
                  </icon-btn>
                  <icon-btn
                      v-else
                      type="view"
                      :to="{name: '/viewContract', params: {tradeContractId: row.id}, query: {from: 'public'}}">
                  </icon-btn>
                </icon-btns>
              </template>
            </el-table-column>
          </el-table>
        </template>
      </el-table-column>

      <el-table-column
          width="200"
          prop="name"
          label="募集中の取引契約名"
          :sort-method="buildSort(row => row.name)"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.name"
                :value-id="row.id">
              <template #default="{value}">
                {{ value }}
                <el-tooltip v-if="!row.expireDateIsValid" :content="`募集期間 (${formatDate(row.expireDate)}まで) が終了しました`" :open-delay="1000">
                  <v-icon color="red" class="mt-n1" small>mdi-clock-outline</v-icon>
                </el-tooltip>
              </template>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          min-width="200"
          prop="tradeContractSource.dataComponentModelName"
          label="データ構成モデル名"
          :sort-method="buildSort(row => row.tradeContractSource.dataComponentModelName)"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.tradeContractSource.dataComponentModelName"
                :message="row.tradeContractSource.dataComponentModelDictionaryName"
                placeholder="未選択">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          min-width="130"
          prop="tradeContractSource.dataImplementation.controller.name"
          label="コントローラ"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.tradeContractSource.dataImplementation
            ? row.tradeContractSource.dataImplementation.controller.name
            : null"
                placeholder="未選択">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="80"
          align="center"
          prop="serviceTypeText"
          label="区分"
          sortable>
        <template #default="{row}">
          {{ row.serviceTypeText }}
          <el-tooltip placement="right">
            <template #content>
              {{ account.owner.name }}が{{ row.serviceTypeText }}しています
            </template>
            <template #default>
              <v-icon small color="blue" class="ml-1">mdi-information</v-icon>
            </template>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column
          width="100"
          prop="createdAt"
          label="作成日"
          sortable>
        <template #default="{row}">{{ formatDate(row.createdAt) }}</template>
      </el-table-column>

      <el-table-column
          width="110"
          prop="expireDate"
          label="募集期限"
          sortable>
        <template #default="{row}">{{ formatDate(row.expireDate) }}</template>
      </el-table-column>

      <el-table-column
          width="70"
          align="center"
          column-key="editing"
          v-if="isEditable">
        <template #default="{row}">
          <icon-btns>
            <icon-btn
                type="view"
                :to="{name: '/viewContract', params: {tradeContractId: row.id}}">
            </icon-btn>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>
  </tab-item-template>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import Formatter from "@/utils/Formatter";
import TableInline from "@/components/atoms/TableInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import {mapGetters} from "vuex";
import Multipane from "@/components/atoms/Multipane.vue";
import MultipaneResizer from "@/components/atoms/MultipaneResizer.vue";
import IconBtn from "@/components/molecules/IconBtn.vue";
import IconBtns from "@/components/molecules/IconBtns";
import TradeContractFilter from "@/components/organisms/TradeContractFilter.vue";
import TableUtil from "@/components/pages/contract/TradeContractList/TableUtil";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";

export default {
  name: 'PublicTab',
  mixins: [
    PathInfoMixin,
    Formatter,
    TableUtil,
  ],
  components: {
    TradeContractFilter,
    IconBtns,
    IconBtn,
    TextFieldLike,
    TableInline,
    TabItemTemplate,
    ToolbarInline
  },
  inject: ['tabItemBounds'],
  props: {
    tradeContracts: {
      type: Array,
      required: true
    },
    selection: {
      type: String,
      required: false,
    }
  },
  data() {
    return {
      tradeContractFilter: () => true,
      filterText: '',
      preventSideEffect: false,
    }
  },
  mounted() {
    this.$watch(_ => [this.selection, this.publicTradeContractsView], _ => {
      const row = this.publicTradeContractsView.find(tc => tc.id === this.selection);
      if(row) {
        this.$refs.publicTradeContractsTable.setCurrentRow(row);
      }
    }, {
      immediate: true
    })
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    publicTradeContractsView() {
      return this.tradeContracts
          .filter(tc => tc.status === 'public');
    },
    publicTradeContractsViewFiltered() {
      const textFilter = tc => {
        const f = this.filterText;
        if(f) {
          return tc.id.includes(f) ||
              tc.name.includes(f) ||
              tc.tradeContractSource.dataComponentModelName?.includes(f) ||
              this.formatDate(tc.createdAt).includes(f);
        } else {
          return true;
        }
      };
      return this.publicTradeContractsView.filter(tc => this.tradeContractFilter(tc) && textFilter(tc));
    },
  },
  methods: {
    applyingTradeContractsView(selection) {
      const statuses = ['applying', 'revising', 'trading'];
      return this.tradeContracts
          .filter(tc => statuses.includes(tc.status))
          .filter(tc => tc.baseId === selection.id);
    },
    handleTradeContractSelection(row, oldRow) {
      if(this.preventSideEffect) {
        return;
      }

      const table = this.$refs.publicTradeContractsTable;
      try {
        this.preventSideEffect = true;
        if (oldRow) {
          table.toggleRowExpansion(oldRow, false);
        }
        if (row) {
          table.toggleRowExpansion(row, true);
        }
      } finally {
        this.preventSideEffect = false;
      }

      if(this.selection === row?.id) {
        return;
      }

      this.$router.push({
        ...this.$route,
        query: {
          selection: row?.id
        }
      });
    },
    handleTradeContractExpanded(row, expandedRows) {
      if(this.preventSideEffect) {
        return;
      }

      const table = this.$refs.publicTradeContractsTable;
      if(expandedRows.includes(row)) { // expand
        table.setCurrentRow(row);
      } else { // collapse
        table.setCurrentRow();
      }
    }
  },
}
</script>

<style lang="scss" scoped>
@import "src/theme";

$expiredColor: #EEE;

.public-trade-contract-table ::v-deep tr.el-table__row {
  &.expanded + tr {
    background-color: $tableRowHighlightColor;

    .el-table__cell.el-table__expanded-cell {
      background-color: transparent !important;
      padding: 0 0 0 47px;
    }
  }

  &.expanded.expired-row + tr {
    background-color: tr-background-color-in-current($expiredColor);
  }

  &.expanded.expired-row:hover + tr {
    background-color: tr-background-color-in-hover-and-current($expiredColor);
  }

  &.expired-row {
    @include tr-background-color($expiredColor, true);
  }
}

.applying-trade-contract-table {
  border-width: 1px 0 1px 1px;
  border-style: solid;
  border-color: rgba(0,0,0,.5);

  ::v-deep .id-column-header {
    display: block;
    font-size: 80%;
    line-height: 1.5;
    white-space: pre;
    text-align: center;
  }
}
</style>
